import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  Dispatch,
  SetStateAction,
} from 'react'
import {useLocation} from 'react-router-dom'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {getUserByToken, signout} from './_requests'
import {WithChildren} from '../../../../_metronic/helpers'
import { toast } from 'react-toastify'
import { useMutation } from 'react-query'
import secureLocalStorage from 'react-secure-storage'

type AuthContextProps = {
  auth: AuthModel | undefined
  permission: any
  savePermission: (permission) => void
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: (api_token?: string) => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  permission: authHelper.getPermission(),
  savePermission: () => {},
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [permission, setPermission] = useState<AuthModel | undefined>(authHelper.getPermission())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }
  const savePermission = (permission) => {
    setPermission(permission)
    if (permission) {
      authHelper.setPermission(permission)
    } else {
      authHelper.removePermission()
    }
  }
  const logoutMutation = (api_token: string) => {
    return signout(api_token);
  };
  const { mutateAsync } = useMutation(logoutMutation as any);

  const logout = async (api_token?: string) => {
    if (api_token) {
      try {
        // Make API call to logout endpoint
        await mutateAsync(api_token as any);
        // Handle the success response
        toast.success('You have been logged out Successfully', {
          theme: 'colored',
        });
      } catch (error) {
        // Handle logout error - still clear local storage even if API fails
        console.error('Logout API error:', error);
      } finally {
        // Always clear local storage and redirect regardless of API success/failure
        secureLocalStorage.removeItem('user_permissions');
        localStorage.removeItem('kt-auth-react-v');
        saveAuth(undefined);
        setCurrentUser(undefined);
        savePermission(undefined);
        // Synchronize logout across tabs
        syncLogout();
        // Redirect to login page
        window.location.href = '/auth/login';
      }
    } else {
      // Handle logout when no API token is provided
      secureLocalStorage.removeItem('user_permissions');
      localStorage.removeItem('kt-auth-react-v');
      saveAuth(undefined);
      setCurrentUser(undefined);
      savePermission(undefined);
      // Synchronize logout across tabs
      syncLogout();
      // Redirect to login page
      window.location.href = '/auth/login';
    }
  };
  const syncLogout = () => {
    // Send a storage event to notify other tabs to logout
    window.localStorage.setItem('logout', Date.now().toString());
  };
  useEffect(() => {
    // Event listener to handle logout event from other tabs
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'logout') {
        logout();
      }
    };
    // Attach the event listener
    window.addEventListener('storage', handleStorageChange);
    // Clean up the event listener when the component is unmounted
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  return (
    <AuthContext.Provider value={{auth, permission, savePermission, saveAuth, currentUser, setCurrentUser, logout}}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {auth, setCurrentUser, logout} = useAuth()
  const location = useLocation()
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  const isLandingPage = location.pathname === '/' || location.pathname === '/landing'
  
  // TODO: Restore getUserByToken call when verify-token API is ready
  // Currently bypassed - using auth data directly
  useEffect(() => {
    if (auth && auth.access_token) {
      // Skip token verification - directly use auth data
      if (auth.admin) {
        setCurrentUser(auth.admin as any)
      }
      
      // TODO: Uncomment when verify-token API is ready
      // const requestUser = async (apiToken: string) => {
      //   try {
      //     if (!didRequest.current) {
      //       const {data} = await getUserByToken(apiToken)
      //       if (data) {
      //         setCurrentUser(data)
      //       }
      //     }
      //   } catch (error) {
      //     console.error(error)
      //     if (!didRequest.current) {
      //       logout()
      //     }
      //   } finally {
      //     setShowSplashScreen(false)
      //   }
      //   return () => (didRequest.current = true)
      // }
      // requestUser(auth.access_token)
    } 
    
    setShowSplashScreen(false)
  }, [auth, setCurrentUser])

  // Don't show splash screen for landing pages
  if (isLandingPage) {
    return <>{children}</>
  }

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
